import { useState, useCallback, useEffect } from "react";
import { Dialog, DialogContent, NestedDialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle, User, Briefcase, GraduationCap, FolderOpen, Award, BadgeCheck } from "lucide-react";
import { useCandidateProfile } from "@/hooks/useCandidateProfile";
import { InlineLoader } from "@/components/ui/progressive-loading";
import { ProfileHeader } from "@/components/profile/ProfileHeader";
import { ExperienceSection } from "@/components/profile/ExperienceSection";
import { EducationSection } from "@/components/profile/EducationSection";
import { ProjectsSection } from "@/components/profile/ProjectsSection";
import { CertificationsSection } from "@/components/profile/CertificationsSection";
import { AwardsSection } from "@/components/profile/AwardsSection";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "@/hooks/useTranslation";

interface EnhancedCandidateProfileProps {
  candidateId?: string;
  applicationId?: string;
  jobId?: string;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  candidate?: any; // For passing pre-loaded candidate data
  nested?: boolean; // When true, uses higher z-index for nested dialogs
}

export function EnhancedCandidateProfile({
  candidateId,
  applicationId,
  jobId,
  trigger,
  open,
  onOpenChange,
  candidate,
  nested = false
}: EnhancedCandidateProfileProps) {
  const { t } = useTranslation('ui');
  const [isOpen, setIsOpen] = useState(open || false);
  const [sectionCounts, setSectionCounts] = useState({
    experiences: 0,
    education: 0,
    projects: 0,
    certifications: 0,
    awards: 0
  });

  // Only call the hook if candidateId is provided and no candidate data is passed
  const { data: profile, isLoading, error } = useCandidateProfile(
    candidateId && !candidate ? candidateId : undefined
  );

  // Use passed candidate data or fetched profile
  const candidateData = candidate?.profiles || profile;
  const targetUserId = candidateId || candidate?.application?.candidate_id;

  const handleOpenChange = useCallback((newOpen: boolean) => {
    setIsOpen(newOpen);
    onOpenChange?.(newOpen);
  }, [onOpenChange]);

  // Sync with external open prop
  useEffect(() => {
    if (open !== undefined) {
      setIsOpen(open);
    }
  }, [open]);

  // Fetch section counts when dialog opens
  useEffect(() => {
    const controller = new AbortController();

    const fetchSectionCounts = async () => {
      if (!targetUserId || !isOpen) return;

      try {
        // Use type assertions to bypass strict typing for these tables
        const [experiencesRes, educationRes, projectsRes, certificationsRes, awardsRes] = await Promise.all([
          (supabase as any).from('user_experiences').select('id', { count: 'exact', head: true }).eq('user_id', targetUserId).abortSignal(controller.signal),
          (supabase as any).from('user_education').select('id', { count: 'exact', head: true }).eq('user_id', targetUserId).abortSignal(controller.signal),
          (supabase as any).from('user_projects').select('id', { count: 'exact', head: true }).eq('user_id', targetUserId).abortSignal(controller.signal),
          (supabase as any).from('user_certifications').select('id', { count: 'exact', head: true }).eq('user_id', targetUserId).abortSignal(controller.signal),
          (supabase as any).from('user_awards').select('id', { count: 'exact', head: true }).eq('user_id', targetUserId).abortSignal(controller.signal)
        ]);

        if (!controller.signal.aborted) {
          setSectionCounts({
            experiences: experiencesRes.count || 0,
            education: educationRes.count || 0,
            projects: projectsRes.count || 0,
            certifications: certificationsRes.count || 0,
            awards: awardsRes.count || 0
          });
        }
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching section counts:', error);
        }
      }
    };

    fetchSectionCounts();

    return () => {
      controller.abort(); // Cancel pending requests on unmount/close
    };
  }, [targetUserId, isOpen]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <InlineLoader size="lg" text={t('loading.candidate_profile')} />
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center p-8">
          <div className="text-center space-y-2">
            <AlertCircle className="w-8 h-8 text-destructive mx-auto" />
            <p className="text-sm text-muted-foreground">{t('candidate_profile.failed_to_load_profile')}</p>
            <p className="text-xs text-destructive">{error}</p>
          </div>
        </div>
      );
    }

    if (!candidateData && !targetUserId) {
      return (
        <div className="flex items-center justify-center p-8">
          <div className="text-center space-y-2">
            <User className="w-8 h-8 text-muted-foreground mx-auto" />
            <p className="text-sm text-muted-foreground">{t('candidate_profile.no_profile_found')}</p>
          </div>
        </div>
      );
    }

    // Create a mock user object for the ProfileHeader
    const mockUser = {
      id: targetUserId,
      email: candidateData?.email || candidate?.application?.guest_email
    };

    return (
      <div className="space-y-6">
        {/* Profile Header */}
        <ProfileHeader
          profile={candidateData}
          user={mockUser}
          isEditing={false}
          onEditToggle={() => {}}
          onProfileUpdate={() => {}}
          readOnly={true}
        />

        {/* Profile Sections */}
        <div className="grid gap-4">
          {/* Experience Section */}
          {sectionCounts.experiences > 0 && (
            <Card className="hover:shadow-sm transition-shadow">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-sm font-medium">
                  <Briefcase className="w-4 h-4 text-muted-foreground" />
                  {t('candidate_profile.work_experience')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ExperienceSection userId={targetUserId} isEditing={false} />
              </CardContent>
            </Card>
          )}

          {/* Education Section */}
          {sectionCounts.education > 0 && (
            <Card className="hover:shadow-sm transition-shadow">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-sm font-medium">
                  <GraduationCap className="w-4 h-4 text-muted-foreground" />
                  {t('candidate_profile.education')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <EducationSection userId={targetUserId} isEditing={false} />
              </CardContent>
            </Card>
          )}

          {/* Projects Section */}
          {sectionCounts.projects > 0 && (
            <Card className="hover:shadow-sm transition-shadow">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-sm font-medium">
                  <FolderOpen className="w-4 h-4 text-muted-foreground" />
                  {t('candidate_profile.projects')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ProjectsSection userId={targetUserId} isEditing={false} />
              </CardContent>
            </Card>
          )}

          {/* Certifications Section */}
          {sectionCounts.certifications > 0 && (
            <Card className="hover:shadow-sm transition-shadow">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-sm font-medium">
                  <BadgeCheck className="w-4 h-4 text-muted-foreground" />
                  {t('candidate_profile.certifications')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CertificationsSection userId={targetUserId} isEditing={false} />
              </CardContent>
            </Card>
          )}

          {/* Awards Section */}
          {sectionCounts.awards > 0 && (
            <Card className="hover:shadow-sm transition-shadow">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-sm font-medium">
                  <Award className="w-4 h-4 text-muted-foreground" />
                  {t('candidate_profile.awards_honors')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <AwardsSection userId={targetUserId} isEditing={false} />
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  };

  const ContentComponent = nested ? NestedDialogContent : DialogContent;

  const DialogComponent = (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <ContentComponent
        className="w-[calc(100vw-4rem)] h-[calc(100vh-4rem)] max-w-none max-h-none bg-background"
        style={{
          width: "calc(100vw - 4rem)",
          height: "calc(100vh - 4rem)",
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          willChange: 'scroll-position',
        }}
      >
        <DialogHeader>
          <DialogTitle></DialogTitle>
        </DialogHeader>
        {renderContent()}
      </ContentComponent>
    </Dialog>
  );

  if (trigger) {
    return (
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          {trigger}
        </DialogTrigger>
        <ContentComponent
          className="w-[calc(100vw-4rem)] h-[calc(100vh-4rem)] max-w-none max-h-none bg-background"
          style={{
            width: "calc(100vw - 4rem)",
            height: "calc(100vh - 4rem)",
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            willChange: 'scroll-position',
          }}
        >
          <DialogHeader>
            <DialogTitle>Candidate Profile</DialogTitle>
          </DialogHeader>
          {renderContent()}
        </ContentComponent>
      </Dialog>
    );
  }

  return DialogComponent;
}
